import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { colors, fontSize } from "style";
import { PrimaryLink, DestructableLink } from "components/Links";
import { shortenText } from "utils";

/**
 * FileDropzone
 * A multi-file drag-and-drop upload component
 * Stores files in component state until form submission
 *
 * @param {Array}      value - File objects, or references to stored files; an entry with a url is shown as a link
 * @param {Function}   onChange - Callback function
 * @param {String}     name - Field name
 * @param {String}     accept - Comma-separated MIME types and extensions to accept (default: "application/pdf")
 * @param {Number}     maxFiles - Maximum number of files allowed (default: 5)
 * @param {Number}     maxSize - Maximum file size in bytes (default: 10MB)
 * @param {String}     dropzoneTextId - Intl message ID for dropzone text
 * @param {String}     hintFileTypeLabel - Optional user-facing file type label for the hint text
 * @param {Boolean}    hasError - Indicates if field has validation error from form
 * @param {Boolean}    disabled - Whether to apply the disable styling to the dropzone and disable action
 */
const FileDropzone = ({
  value = [],
  onChange,
  name,
  accept = "application/pdf",
  maxFiles = 5,
  maxSize = 20 * 1024 * 1024, // 20MB,
  dropzoneTextId = "FileDropzone.DragAndDrop",
  hintFileTypeLabel,
  hasError = false,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInput = useRef(null);

  const files = Array.isArray(value) ? value : [];

  const getHintFileType = () => {
    if (hintFileTypeLabel) return hintFileTypeLabel;

    switch (accept) {
      case "application/pdf":
        return "PDF";
      case "video/*":
        return "video";
      default:
        return "file";
    }
  };

  const isAccepted = (file) =>
    !accept ||
    accept
      .split(",")
      .some((token) => (token.startsWith(".") ? file.name.toLowerCase().endsWith(token) : file.type.match(token)));

  const validateFile = (file) => {
    const validationErrors = [];

    // Check file type
    if (!isAccepted(file)) {
      validationErrors.push({
        message: `${file.name}: Only ${getHintFileType()} files are allowed`,
      });
    }

    // Check file size
    if (file.size > maxSize) {
      validationErrors.push({
        message: `${file.name}: File size must be less than ${maxSize / (1024 * 1024)}MB`,
      });
    }

    return validationErrors;
  };

  const handleFiles = (newFiles) => {
    if (disabled) return;

    const fileArray = Array.from(newFiles);
    const validationErrors = [];

    // Check total files limit
    if (files.length + fileArray.length > maxFiles) {
      validationErrors.push({
        message: `Maximum ${maxFiles} files allowed`,
      });
      // Pass validation error to form system
      onChange(name, files, validationErrors);
      return;
    }

    // Validate each file
    fileArray.forEach((file) => {
      const fileErrors = validateFile(file);
      validationErrors.push(...fileErrors);
    });

    if (validationErrors.length > 0) {
      // Pass validation error to form system
      onChange(name, files, validationErrors);
      return;
    }

    // Clear errors and add files
    const updatedFiles = [...files, ...fileArray];
    onChange(name, updatedFiles);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  };

  const handleFileSelect = (e) => {
    if (disabled) {
      e.target.value = "";
      return;
    }

    const selectedFiles = e.target.files;
    if (selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    if (disabled) return;

    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(name, updatedFiles);
  };

  const handleBrowseClick = () => {
    if (disabled) return;

    fileInput.current.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div css={styles.container}>
      <div
        css={styles.dropzone(isDragging, files.length > 0, hasError, disabled)}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Icon type={ICON_TYPE.upload} size={ICON_SIZE.xxxlarge} color={colors.grayAnatomyLight1} />
        <div css={styles.dropzoneText}>
          <FormattedMessage id={dropzoneTextId} />
        </div>
        <div css={styles.dropzoneSubtext}>
          <FormattedMessage id="FileDropzone.Or" />{" "}
          <PrimaryLink onClick={handleBrowseClick} disabled={disabled}>
            <FormattedMessage id="FileDropzone.Browse" />
          </PrimaryLink>
        </div>
        <div css={styles.dropzoneHint}>
          <FormattedMessage
            id="FileDropzone.Hint"
            values={{
              fileTypeLabel: getHintFileType(),
              maxFiles,
              maxSize: maxSize / (1024 * 1024),
            }}
          />
        </div>
      </div>

      {files.length > 0 && (
        <div css={styles.filesContainer}>
          <div css={styles.filesHeader}>
            <FormattedMessage id="FileDropzone.SelectedFiles" values={{ count: files.length }} />
          </div>
          {files.map((file, index) => (
            <div key={index} css={styles.fileItem}>
              <div css={styles.fileInfo}>
                <Icon type={ICON_TYPE.file} size={ICON_SIZE.large} color={colors.purpleRainBase} />
                <div css={styles.fileDetails}>
                  {file.url ? (
                    <PrimaryLink to={file.url} target="_blank" css={styles.fileName}>
                      {shortenText(file.name || file.value, 40, false, true)}
                    </PrimaryLink>
                  ) : (
                    <div css={styles.fileName}>{shortenText(file.name || file.value, 40, false, true)}</div>
                  )}
                  {file.size >= 0 && <div css={styles.fileSize}>{formatFileSize(file.size)}</div>}
                </div>
              </div>
              {!disabled && (
                <DestructableLink onClick={() => handleRemoveFile(index)}>
                  <Icon type={ICON_TYPE.close} size={ICON_SIZE.medium} color="inherit" />
                </DestructableLink>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept={accept}
        multiple
        onChange={handleFileSelect}
        disabled={disabled}
        css={styles.hiddenInput}
      />
    </div>
  );
};

const styles = {
  container: css`
    order: 2;
    width: 100%;
  `,
  dropzone: (isDragging, hasFiles, hasError, disabled) => css`
    border: 2px dashed ${hasError ? colors.red : isDragging ? colors.purpleRainBase : colors.grayAnatomyLight1};
    border-radius: 0.8rem;
    padding: 3rem;
    text-align: center;
    background-color: ${disabled ? colors.grayAnatomyLight6 : isDragging ? colors.grayAnatomyLight5 : colors.white};
    transition: all 0.2s ease;
    cursor: ${disabled ? "not-allowed" : "pointer"};
    opacity: ${disabled ? 0.7 : 1};
    margin-bottom: ${hasFiles ? "2rem" : "0"};

    &:hover {
      border-color: ${disabled
        ? hasError
          ? colors.red
          : colors.grayAnatomyLight1
        : hasError
        ? colors.red
        : colors.purpleRainBase};
      background-color: ${disabled ? colors.grayAnatomyLight6 : colors.grayAnatomyLight5};
    }
  `,
  dropzoneText: css`
    margin-top: 1rem;
    font-size: ${fontSize.medium};
    font-weight: 600;
    color: ${colors.grayAnatomyLight1};
  `,
  dropzoneSubtext: css`
    margin-top: 0.5rem;
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyLight1};
  `,
  dropzoneHint: css`
    margin-top: 1rem;
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
  filesContainer: css`
    border: 1px solid ${colors.grayAnatomyLight4};
    border-radius: 0.8rem;
    overflow: hidden;
  `,
  filesHeader: css`
    padding: 1rem 1.5rem;
    background-color: ${colors.grayAnatomyLight5};
    font-size: ${fontSize.small};
    font-weight: 600;
    color: ${colors.grayAnatomyLight1};
    border-bottom: 1px solid ${colors.grayAnatomyLight4};
  `,
  fileItem: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.2rem 1.5rem;
    border-bottom: 1px solid ${colors.grayAnatomyLight4};

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${colors.grayAnatomyLight5};
    }
  `,
  fileInfo: css`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  `,
  fileDetails: css`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  `,
  fileName: css`
    font-size: ${fontSize.small};
    font-weight: 500;
    color: ${colors.grayAnatomyLight1};
  `,
  fileSize: css`
    font-size: ${fontSize.xsmall};
    color: ${colors.grayAnatomyLight1};
  `,
  hiddenInput: css`
    display: none;
  `,
};

FileDropzone.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  accept: PropTypes.string,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  dropzoneTextId: PropTypes.string,
  hintFileTypeLabel: PropTypes.string,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default FileDropzone;
